/** Plain-English packed counts and multi-pile waste walks. */

import { roundWalkQty } from "./packedAuditMath";
import { bareSpokenToken, parseSpokenQty } from "./spokenNumberWords";

export type SpokenWalkDraft = {
  primaryQty: number;
  secondaryQty: number | null;
  materialName: string;
  raw: string;
  /** Pallet / bundle count, when they counted shipping units. */
  shippingUnits?: number;
  /** Pieces on each shipping unit, when they said so. */
  piecesEach?: number;
  shippingUnitWord?: string;
};

const SHIP_UNITS = new Set(["pallet", "pallets", "bundle", "bundles", "skid", "skids", "pack", "packs"]);
const PIECE_UNITS = new Set(["sheet", "sheets", "piece", "pieces", "board", "boards", "panel", "panels", "each"]);
const SIZE_NEXT = new Set(["by", "x", "light", "lite", "wide"]);
const NAME_DROP = new Set([
  "packed", "of", "item", "items", "each", "both", "a", "an", "the", "and", "warehouse",
]);

function qtyAt(tokens: string[], index: number): { value: number; next: number } | null {
  const parsed = parseSpokenQty(tokens.slice(index));
  if (!parsed) return null;
  return { value: parsed.value, next: index + parsed.consumed };
}

function isSizeQty(tokens: string[], index: number, consumed: number): boolean {
  const next = bareSpokenToken(tokens[index + consumed] ?? "");
  if (SIZE_NEXT.has(next)) return true;
  const prev = bareSpokenToken(tokens[index - 1] ?? "");
  return prev === "by" || prev === "x";
}

function takeUnit(
  tokens: string[],
  units: Set<string>,
  drop: Set<number>,
): { value: number; word: string } | null {
  for (let i = 0; i < tokens.length; i++) {
    if (drop.has(i)) continue;
    const qty = qtyAt(tokens, i);
    if (!qty || isSizeQty(tokens, i, qty.next - i)) continue;
    const unitAt = qty.next;
    if (drop.has(unitAt)) continue;
    const word = bareSpokenToken(tokens[unitAt] ?? "");
    if (!units.has(word)) continue;
    for (let j = i; j <= unitAt; j++) drop.add(j);
    if (bareSpokenToken(tokens[unitAt + 1] ?? "") === "each") drop.add(unitAt + 1);
    return { value: qty.value, word };
  }
  return null;
}

function takeBay(tokens: string[], drop: Set<number>): number | null {
  for (let i = 0; i < tokens.length; i++) {
    if (drop.has(i)) continue;
    const word = bareSpokenToken(tokens[i] ?? "");
    if (word !== "bay" && word !== "bays") continue;
    const after = qtyAt(tokens, i + 1);
    if (after && !drop.has(i + 1) && !isSizeQty(tokens, i + 1, after.next - (i + 1))) {
      drop.add(i);
      for (let j = i + 1; j < after.next; j++) drop.add(j);
      return after.value;
    }
    let j = i - 1;
    const fillers: number[] = [];
    while (j >= 0 && (bareSpokenToken(tokens[j] ?? "") === "in" || bareSpokenToken(tokens[j] ?? "") === "the")) {
      fillers.push(j);
      j -= 1;
    }
    if (j < 0 || drop.has(j)) continue;
    const before = qtyAt(tokens, j);
    const boundary = fillers.length > 0 ? Math.min(...fillers) : i;
    if (!before || before.next !== boundary) continue;
    for (let k = j; k <= i; k++) drop.add(k);
    return before.value;
  }
  return null;
}

function takeCount(tokens: string[], drop: Set<number>): number | null {
  for (let i = 0; i < tokens.length; i++) {
    if (drop.has(i)) continue;
    const qty = qtyAt(tokens, i);
    if (!qty || isSizeQty(tokens, i, qty.next - i)) continue;
    for (let j = i; j < qty.next; j++) drop.add(j);
    return qty.value;
  }
  return null;
}

function foldSwing(tokens: string[], drop: Set<number>): "inswing" | "outswing" | null {
  for (let i = 0; i < tokens.length; i++) {
    const word = bareSpokenToken(tokens[i] ?? "");
    if (word === "inswing" || word === "outswing") {
      drop.add(i);
      return word;
    }
    const next = bareSpokenToken(tokens[i + 1] ?? "");
    if ((word === "in" || word === "out") && next === "swing") {
      drop.add(i);
      drop.add(i + 1);
      return word === "in" ? "inswing" : "outswing";
    }
  }
  return null;
}

function materialFrom(tokens: string[], drop: Set<number>, swing: string | null): string {
  const name = tokens
    .filter((_, index) => !drop.has(index))
    .map((token) => token.trim())
    .filter((token) => token.length > 0 && !NAME_DROP.has(bareSpokenToken(token)))
    .join(" ")
    .replace(/[.,;:]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!swing || name.toLowerCase().includes(swing)) return name;
  return `${name} ${swing}`.trim();
}

/** Warehouse speech: pallets, sheets each, a place name, inswing. */
export function parsePackedPlain(raw: string): SpokenWalkDraft | null {
  let text = raw.replace(/[’‘]/g, "'").trim();
  text = text.replace(/^(?:i'm|i am|im)\s+looking\s+at\s+/i, "");
  text = text.replace(/^packed\s+[^.?!]*?\bwarehouse\b[.?!]?\s*/i, "");
  const tokens = text.split(/\s+/).filter((token) => token.length > 0);
  if (tokens.length === 0) return null;
  const drop = new Set<number>();
  const ship = takeUnit(tokens, SHIP_UNITS, drop);
  const pieces = takeUnit(tokens, PIECE_UNITS, drop);
  const bay = takeBay(tokens, drop);
  const swing = foldSwing(tokens, drop);
  const eachCount = ship ? null : takeCount(tokens, drop);
  const name = materialFrom(tokens, drop, swing);
  if (!name) return null;
  let primary: number | null = null;
  if (ship && pieces) primary = roundWalkQty(ship.value * pieces.value);
  else if (eachCount != null) primary = eachCount;
  else if (ship) primary = ship.value;
  if (primary == null) return null;
  return {
    primaryQty: primary,
    secondaryQty: bay,
    materialName: name,
    raw: raw.trim(),
    shippingUnits: ship?.value,
    piecesEach: pieces?.value,
    shippingUnitWord: ship?.word,
  };
}

export function resolvePackedPieceQty(
  draft: SpokenWalkDraft,
  material?: { shipping_unit?: string; pieces_per_shipping_unit?: number | null } | null,
): { qty: number; note: string | null } {
  const units = draft.shippingUnits;
  const each = draft.piecesEach;
  const word = draft.shippingUnitWord ?? "units";
  if (units != null && each != null) {
    const qty = roundWalkQty(units * each);
    return { qty, note: `${units} ${word} × ${each} = ${qty}` };
  }
  const per = Number(material?.pieces_per_shipping_unit ?? 0);
  if (units != null && per > 1) {
    const qty = roundWalkQty(units * per);
    const ship = material?.shipping_unit?.trim() || "shipping unit";
    return { qty, note: `${units} ${word} × ${per} per ${ship} = ${qty}` };
  }
  if (units != null) {
    return {
      qty: units,
      note: `Heard ${units} ${word}. This material has no pieces per shipping unit, so the form shows ${units}.`,
    };
  }
  return { qty: draft.primaryQty, note: null };
}
